/**
 * The daily login streak — one status read and one claim, shared by the popup that offers it
 * and the Daily Rewards page that keeps it claimable.
 *
 * The popup is a one-shot offer: close it and it is gone until the next app open. The page
 * reads the SAME query, so a streak that was dismissed is still there to take.
 */
import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { claimDailyLoginReward, fetchDailyLoginStatus } from "../api/dailyLoginApi";
import { useWalletConnection } from "../auth/useWalletConnection";
import { translate } from "../i18n/translate";

/** Status rarely changes within a session; one read per app open is the whole point. */
const DAILY_LOGIN_STALE_MS = 5 * 60 * 1000;


export const dailyLoginStatusQueryKey = (address: string | null | undefined) =>
  ["dailyLoginStatus", address] as const;

export function useDailyLogin() {
  const { isConnected, address } = useWalletConnection();
  const queryClient = useQueryClient();
  const [claiming, setClaiming] = useState(false);
  const [paid, setPaid] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: status } = useQuery({
    queryKey: dailyLoginStatusQueryKey(address),
    queryFn: fetchDailyLoginStatus,
    enabled: isConnected && !!address,
    staleTime: DAILY_LOGIN_STALE_MS,
  });

  const claim = useCallback(async (): Promise<boolean> => {
    setClaiming(true);
    setError(null);
    try {
      const result = await claimDailyLoginReward();
      if (!result.success) {
        setError(result.error ?? translate("progression.daily.claimFailed"));
        return false;
      }
      setPaid(result.reward ?? 0);
      void queryClient.invalidateQueries({ queryKey: ["trainerProfile"] });
      void queryClient.invalidateQueries({ queryKey: ["dailyLoginStatus"] });
      return true;
    } catch {
      setError(translate("progression.daily.claimFailed"));
      return false;
    } finally {
      setClaiming(false);
    }
  }, [queryClient]);

  return { isConnected, status, claim, claiming, paid, error };
}
